import { getOwaWorkload, OwaWorkload } from 'owa-workloads';
import {
    getCompletedTaskCustomData,
    getFolderCustomData,
    getPanelCloseCustomData,
    getTasksCustomData,
} from './selectors/telemetry';
import { getMailAriaTenantTokens } from 'owa-analytics';
import getStore from './store/store';

export interface PanelCloseCustomData {
    sessionDuration: number;
}

export interface TasksCustomData {
    activeTasks: number;
    activeTasksWithDueDate: number;
    activeTasksWithReminders: number;
}

interface PanelOpenCustomData {
    workLoad: OwaWorkload;
}

export interface FolderCustomData {
    folders: number;
    sharedFolders: number;
}

export interface CompletedTaskCustomData {
    differenceFromDueDate: number;
    differenceFromReminderDate: number;
}

const todoOptions = {
    isCore: true,
    tenantTokens: getMailAriaTenantTokens(),
};

export const Datapoints = {
    /**
     * Todo Datapoint general info:
     *
     * Todo datapoints are unsampled, so all datapoints are logged for 100% of users
     *
     * The data is stored in the calendar tenant
     *
     * A todo panel session is the period of time between a panel open and panel close. Each time the panel opens
     *  this is considered a new session, and a todo panel session ID is created. This ID is logged in the custom
     * data of each todo datapoint, so each datapoint can be associated with a particular panel session.
     *
     * There are three types of todo datapoints:
     * 1. task action datapoints (prefixed with todoItemActions): actions that the user can take on the todo, i.e. star, complete, etc.
     * 2. panel action datapoints (prefixed with todoPanelAction): actions that the user takes on the panel
     * level, i.e change folder, open panel, close panel, etc.
     * 3. metadata collection datapoints (prefixed with todoMetaData): Data about the users tasks and folders,
     * i.e. how many folders they have.
     */

    /**
     * todoItemActionContextMenuEditSubject
     * logged: when the "Edit Subject" context menu option is clicked.
     */
    todoItemActionContextMenuEditSubject: {
        name: 'todoItemActionContextMenuEditSubject',
        customData: todoCustomData(),
        options: todoOptions,
    },
    /**
     * todoItemActionContextMenuRemoveImportance
     * logged: when the "Remove Importance" context menu option is clicked
     */
    todoItemActionContextMenuRemoveImportance: {
        name: 'todoItemActionContextMenuRemoveImportance',
        customData: todoCustomData(),
        options: todoOptions,
    },
    /**
     * todoItemActionContextMenuMarkImportant
     * logged: when the "Mark as Important" context menu option is clicked
     */
    todoItemActionContextMenuMarkImportant: {
        name: 'todoItemActionContextMenuMarkImportant',
        customData: todoCustomData(),
        options: todoOptions,
    },
    /**
     * todoItemActionMarkComplete
     * logged: when a todo is marked as complete from the context menu or the checkbox
     */
    todoItemActionMarkComplete: () => ({
        name: 'todoItemActionMarkComplete',
        options: todoOptions,
        customData: todoCustomData(),
    }),
    /**
     * todoItemActionMarkIncomplete
     * logged: when a todo is marked as incomplete from the context menu or the checkbox
     */
    todoItemActionMarkIncomplete: () => ({
        name: 'todoItemActionMarkIncomplete',
        options: todoOptions,
        customData: todoCustomData(),
    }),
    /**
     * todoItemActionMarkCompleteDropOnRecentlyCompleted
     * logged: when a todo is marked as complete by dropping on recently completed list
     */
    todoItemActionMarkCompleteDropOnRecentlyCompleted: {
        datapoint: () => ({
            name: 'todoItemActionMarkCompleteDropOnRecentlyCompleted',
            options: todoOptions,
            customData: todoCustomData(),
        }),
    },
    /**
     * todoItemActionCreateFromEmail
     * logged: when a todo is created by dragging an email into the todo pane from the mail list
     */
    todoItemActionCreateFromEmail: {
        datapoint: () => ({
            name: 'todoItemActionCreateFromEmail',
            options: todoOptions,
            customData: todoCustomData(),
        }),
    },
    /**
     * todoItemActionContextMenuDelete
     * logged: when a todo is deleted via the context menu option
     */
    todoItemActionContextMenuDelete: () => ({
        name: 'todoItemActionContextMenuDelete',
        options: todoOptions,
        customData: todoCustomData(),
    }),
    /**
     * todoItemActionCreate
     * logged: when the user creates a todo
     */
    todoItemActionCreate: {
        datapoint: () => ({
            name: 'todoItemActionCreate',
            options: todoOptions,
            customData: todoCustomData(),
        }),
    },
    /**
     * todoItemActionUpdateSubject
     * logged: when the todo subject is updated. This happens when the user clicks away from the todo they are editing,
     * or when they key "enter" after editing a todo.
     */
    todoItemActionUpdateSubject: {
        name: 'todoItemActionUpdateSubject',
        customData: todoCustomData(),
        options: todoOptions,
    },
    /**
     * todoItemActionReorder
     * logged: when a todo is reordered in the list (logged on drop)
     */
    todoItemActionReorder: {
        datapoint: () => ({
            name: 'todoItemActionReorder',
            options: todoOptions,
            customData: todoCustomData(),
        }),
    },
    /**
     * todoItemActionDropOnEndOfListSpacer
     * logged: when a todo is dropped on the end of the list spacer
     */
    todoItemActionDropOnEndOfListSpacer: {
        datapoint: () => ({
            name: 'todoItemActionDropOnEndOfListSpacer',
            options: todoOptions,
            customData: todoCustomData(),
        }),
    },
    /**
     * todoItemActionMoveUp
     * logged: when a todo is moved one position up in the list
     */
    todoItemActionMoveUp: {
        datapoint: () => ({
            name: 'todoItemActionMoveUp',
            options: todoOptions,
            customData: todoCustomData(),
        }),
    },
    /**
     * todoItemActionMoveDown
     * logged: when a todo is moved one position down in the list
     */
    todoItemActionMoveDown: {
        datapoint: () => ({
            name: 'todoItemActionMoveDown',
            options: todoOptions,
            customData: todoCustomData(),
        }),
    },
    /**
     * todoItemActionMoveToTop
     * logged: when a todo is moved to the top of the list
     */
    todoItemActionMoveToTop: {
        datapoint: () => ({
            name: 'todoItemActionMoveToTop',
            options: todoOptions,
            customData: todoCustomData(),
        }),
    },
    /**
     * todoItemActionMoveToBottom
     * logged: when a todo is moved to the bottom of the list
     */
    todoItemActionMoveToBottom: {
        datapoint: () => ({
            name: 'todoItemActionMoveToBottom',
            options: todoOptions,
            customData: todoCustomData(),
        }),
    },
    /**
     * todoItemActionUnstar
     * logged: when filled star icon is clicked
     */
    todoItemActionUnstar: {
        name: 'todoItemActionUnstar',
        customData: todoCustomData(),
        options: todoOptions,
    },
    /**
     * todoItemActionStar
     * logged: when the unfilled star icon is clicked
     */
    todoItemActionStar: {
        name: 'todoItemActionStar',
        options: todoOptions,
        customData: todoCustomData(),
    },
    /**
     * todoItemActionReminderEdited
     * logged: when a reminder is edited
     */
    todoItemActionReminderEdited: {
        name: 'todoItemActionReminderEdited',
        options: todoOptions,
        customData: todoCustomData(),
    },
    /**
     * todoItemActionDueDateEdited
     * logged: when a due date is edited
     */
    todoItemActionDueDateEdited: {
        name: 'todoItemActionDueDateEdited',
        options: todoOptions,
        customData: todoCustomData(),
    },
    /**
     * todoItemActionViewEmail
     * logged: when view email entity is clicked
     */
    todoItemActionViewEmail: () => ({
        name: 'todoItemActionViewEmail',
        options: todoOptions,
        customData: todoCustomData(),
    }),

    /** 2. panel action datapoints */
    /**
     * todoPanelActionOpen
     * logged: when the panel is opened
     */
    todoPanelActionOpen: {
        datapoint: () => ({
            name: 'todoPanelActionOpen',
            options: todoOptions,
            customData: todoCustomData({
                ...getTasksCustomData(),
                workLoad: getOwaWorkload(),
            }),
        }),
    },
    /**
     * todoPanelActionClose
     * logged: when the todo panel is closed/ dismissed (via "X" or light dismiss)
     * customData: sessionDuration - the amount of time the todo panel was open. (session duration
     * measures from panel open to panel close, each time the panel opens this is considered a new session)
     */
    todoPanelActionClose: {
        datapoint: () => ({
            name: 'todoPanelActionClose',
            options: todoOptions,
            customData: todoCustomData(getPanelCloseCustomData()),
        }),
    },
    /**
     * todoPanelActionChangeFolder
     * logged: when folder is changed
     */
    todoPanelActionChangeFolder: {
        datapoint: () => ({
            name: 'todoPanelActionChangeFolder',
            options: todoOptions,
            customData: todoCustomData(),
        }),
    },
    /**
     * todoPanelActionShowRecentlyCompleted
     * logged: when show recently completed tasks is clicked
     */
    todoPanelActionShowRecentlyCompleted: {
        datapoint: () => ({
            name: 'todoPanelActionShowRecentlyCompleted',
            options: todoOptions,
            customData: todoCustomData(),
        }),
    },
    /**
     * todoPanelActionHideRecentlyCompleted
     * logged: when show recently completed tasks is clicked
     */
    todoPanelActionHideRecentlyCompleted: {
        datapoint: () => ({
            name: 'todoPanelActionHideRecentlyCompleted',
            options: todoOptions,
            customData: todoCustomData(),
        }),
    },
    /**
     * todoPanelManageAllTasks
     * logged: when manage all tasks is clicked to open To-Do app
     */
    todoPanelManageAllTasks: {
        name: 'todoPanelManageAllTasks',
        options: () => todoOptions,
        customData: () => todoCustomData(),
    },

    /** 3. todo metadata datapoints */
    /**
     * todoMetaDataFolder
     * logged: when the user opens the panel
     * customData: folders, sharedFolders - counts for the number of folders and shared folders the user has
     */
    todoMetaDataFolder: {
        name: 'todoMetaDataFolder',
        options: todoOptions,
        customData: () => todoCustomData(getFolderCustomData()),
    },
    /**
     * todoMetaDataTasks
     * logged: when the user changes folders
     * customData: activeTasks, activeTasksWithDueDate, activeTasksWithReminders - counts for the number of active tasks,
     * active tasks with due date, and active tasks with reminders the user has in there currently selected todo folder
     */
    todoMetaDataTasks: {
        name: 'todoMetaDataTasks',
        options: todoOptions,
        customData: () => todoCustomData(getTasksCustomData()),
    },
    /**
     * todoMetaDataCompletedTask
     * logged: when a user completes a task
     * customData: differenceFromDueDate, differenceFromReminderDate - difference in time (in milliseconds) between the time when the task was completed and its due date or Reminder Date.
     */
    todoMetaDataCompletedTask: {
        name: 'todoMetaDataCompletedTask',
        options: todoOptions,
        customData: (todoId: string) => todoCustomData(getCompletedTaskCustomData(todoId)),
    },

    /** 4. Performance datapoints */
    /**
     * todoPerformancePanelLoad
     * logged: to measure performance of panel load with the tasks/folder requests
     */
    todoPerformancePanelLoad: {
        name: 'todoPerformancePanelLoad',
        options: todoOptions,
    },
    /**
     * todoPerformanceFolderSwitch
     * logged: to measure performance of folder switch
     */
    todoPerformanceFolderSwitch: {
        name: 'todoPerformanceFolderSwitch',
        options: todoOptions,
    },
};

export const PerformanceDatapointCheckmarks = {
    fetchedActiveTasks: 'fetchedActiveTasks',
    processedActiveTasks: 'processedActiveTasks',
    fetchedCompletedTasks: 'fetchedCompletedTasks',
    processedCompletedTasks: 'processedCompletedTasks',
    fetchedTaskFolders: 'fetchedTaskFolders',
    processedTaskFolders: 'processedTaskFolders',
};

export function todoCustomData(
    actionSpecificCustomData?:
        | PanelCloseCustomData
        | FolderCustomData
        | CompletedTaskCustomData
        | TasksCustomData
        | (TasksCustomData & PanelOpenCustomData)
) {
    const store = getStore();
    return {
        ...actionSpecificCustomData,
        panelSessionId: store.panelId,
        parentSessionId: store.parentSessionId,
        source: store.source,
    };
}
